import React, { useEffect, useRef, useState } from 'react'

import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import classNames from 'classnames'
import _ from 'lodash'
import usePlacesAutocomplete from 'use-places-autocomplete'
import { Button } from 'ziphy-web-shared/basic/lib/buttons'
import { Input } from 'ziphy-web-shared/basic/lib/forms/fields'
import { BubbleLoader } from 'ziphy-web-shared/basic/lib/loaders'
import { ClickOutside, showAlert, TransitionWrapper } from 'ziphy-web-shared/basic/lib/utilities'

import maps from '@library/map/Map'
import SelectCountryFlag from '@library/selectCountry/SelectCountryFlag'

import { extendPlace } from '@helpers/addressHelper'

import styles from './AddPlaceModalSearchPlace.module.scss'

const AddPlaceModalSearchPlace = (props) => {
  const { t } = useTranslation()

  const { place, country, isDetails, onChange, toggleView } = props

  const inputRef = useRef()

  const [showPopup, setShowPopup] = useState(false)

  const config = getConfig(country.value)
  const autoComplete = usePlacesAutocomplete(config)
  const { ready, value, setValue, suggestions, clearSuggestions } = autoComplete
  const [tmpValue, setTmpValue] = useState(value)

  useEffect(() => {
    let result = ''

    if (place) {
      result = extendPlace(place).address
    }

    setValue(result, false)
    setTmpValue(result)
  }, [place]) // eslint-disable-line react-hooks/exhaustive-deps

  async function selectPlace(item) {
    clearSuggestions()
    onChange({ geocoderId: item.place_id, updateHookCoords: true })
  }

  function clearInput() {
    clearSuggestions()
    inputRef.current.focus()
  }

  async function handleDetect() {
    const { coords, error } = await maps.getUserGeolocation()

    if (coords) {
      onChange({ coords, updateHookCoords: true })
    } else if (error) {
      showAlert.error({
        title: t('addresses.detect_disabled.title'),
        content: t('addresses.detect_disabled.message'),
      })
    }
  }

  return (
    <TransitionWrapper
      items={!isDetails}
      initial={isDetails}
      from={{ opacity: 0, height: 0, marginBottom: 0 }}
      enter={{ opacity: 1, height: 'auto', marginBottom: 20 }}
      leave={{ opacity: 0, height: 0, marginBottom: 0 }}
    >
      {(item) =>
        item &&
        ((style) => (
          <div className={styles.submit} style={style}>
            <Button
              mode={['secondary-dark', 'justIcon']}
              leftElement={'location'}
              onClick={handleDetect}
              className={styles.location}
            />
            <SelectCountryFlag country={country.value} onChange={country.onChange} />
            <div className={styles.searchWrapper}>
              <Input
                ref={inputRef}
                innerClassName={styles.search}
                field={{ value, onChange: setValue }}
                clear={true}
                onClear={clearInput}
                onKeyDown={() => setShowPopup(true)}
                onFocus={() => setShowPopup(true)}
                placeholder={maps.isDetailedPlace(place) ? tmpValue : t('add_address.ph.search')}
                mode={['secondary', 'sm']}
                leftElement={'locationMarker'}
                disabled={!ready}
              />
              {showPopup && (suggestions.status !== '' || suggestions.loading) && (
                <ClickOutside className="search-popup" onClick={() => setShowPopup(false)}>
                  <div className="search-popup-scroll">
                    {suggestions.loading && (
                      <div className="search-popup-group-loader">
                        <BubbleLoader mode="lg" />
                      </div>
                    )}
                    {suggestions.data.length > 0
                      ? suggestions.data.map((item) => (
                          <div
                            key={item.place_id}
                            className="search-popup-item"
                            onClick={() => selectPlace(item)}
                          >
                            <span className="search-popup-item-title">
                              {item.structured_formatting.main_text}
                            </span>
                            <span className="search-popup-item-meta">
                              , {item.structured_formatting.secondary_text}
                            </span>
                          </div>
                        ))
                      : suggestions.status !== '' && (
                          <div className="search-popup-error">{t('errors.no_results')}</div>
                        )}
                  </div>
                </ClickOutside>
              )}
            </div>
            <Button
              mode={['primary']}
              disabled={_.isEmpty(value) || _.isEmpty(tmpValue)}
              onClick={toggleView}
              className={styles.submitButton}
            >
              {t('btn.submit_address')}
            </Button>
          </div>
        ))
      }
    </TransitionWrapper>
  )
}

function getConfig(selected) {
  // disabled available because maximum 5 countries
  // if (_.isEmpty(selected)) {
  //   selected = getEnabledCountries(true)
  // }

  return {
    cache: false,
    requestOptions: {
      types: ['premise', 'street_address', 'postal_code'],
      componentRestrictions: {
        country: selected,
      },
    },
    debounce: 400,
  }
}

export default observer(AddPlaceModalSearchPlace)
